import { Box } from "@mui/system";
import { CircularProgress, Divider, Grid, TextField, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";

import { countryOption, genderOptions, genderTitleOptions, maritalStatusOptions } from "../constants";
import FieldDatepicker from "components/forms/fields/FieldDatepicker";
import FieldAutocompleteList from "components/forms/fields/FieldAutocompleteList";
import { useGetAgencies } from "core/agencies/hooks";
import { GridBreak } from "components/GridBreak";
import { EmailRegex } from "core/login/constants";
import { useGetValueOptions } from "core/value_options/hooks";
import { toast } from "react-toastify";
import { useMemo } from "react";

const ProfileForm = () => {
    const {register, control, formState, getValues, watch} = useFormContext<any>();
    const {errors} = formState;
    const {data: agenciesData, isLoading: isAgenciesLoading, isError: isAgenciesError} = useGetAgencies();
    const {data: zipCodes, isLoading: isZCLoading, isError: isZCError} = useGetValueOptions({namespace: 'anael', category: 'zip_codes', onError: (err) => toast.error("une erreur est survenue")});
    const {data: valueOptions, isLoading: isOptionsLoading, isError: isOptionsError} = useGetValueOptions({namespace: 'anael', category: 'document_id', onError: (err) => toast.error("une erreur est survenue")});
    const zipCodesOptions = useMemo(() => zipCodes?.options?.reduce((a: any, v: any) => {a.push([v.value, v.label]); return a;}, []) || [], [zipCodes]);
    const zipCode = watch("address_zip_code");
    const cityEnabled = zipCode !== undefined && zipCode !== null && zipCode !== '';

    const {data: cities, isLoading: isCitiesLoading, isError: isCitiesError} = useGetValueOptions({
        namespace: 'anael',
        category: 'cities',
        parentCategory: 'zip_codes',
        parentValue: zipCode,
        enabled: cityEnabled,
        onError: () => toast.error("une erreur est survenue")
    });
    const citiesOptions = useMemo(() => cities?.options?.reduce((a: any, v: any) => {a.push([v.value, v.label]); return a;}, []) || [], [cities]);

    return (
        <Box>
            <Box>
                <Typography variant="h5">Informations personnelles</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 3}}>
                    <Grid item xs={1}>
                        <TextField
                            id='first_name'
                            label='Prénom'
                            {...register('first_name')}
                            autoComplete='off'
                            error={errors?.first_name !== undefined}
                            helperText={errors?.first_name && errors.first_name.message as string}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='last_name'
                            label='Nom'
                            {...register('last_name')}
                            autoComplete='off'
                            error={errors?.last_name !== undefined}
                            helperText={errors?.last_name && errors.last_name.message as string}
                        />
                    </Grid>
                    {getValues('gender') === 'female' && (<Grid item xs={1}>
                        <TextField
                            id='maiden_name'
                            label='Nom de Jeune Fille'
                            {...register('maiden_name')}
                            autoComplete='off'
                            error={errors?.maiden_name !== undefined}
                            helperText={errors?.maiden_name && errors.maiden_name.message as string}
                        />
                    </Grid>)}
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
                <Typography variant="h5">Contact</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 3}}>
                    <Grid item xs={1} sm={2}>
                        <TextField
                            required
                            id='email'
                            label="Email"
                            {...register('account.email', { pattern: EmailRegex })}
                            autoComplete='off'
                            error={errors?.account && (errors?.account as any).email !== undefined}
                            helperText={errors?.account && (errors.account as any)?.email && (errors.account as any).email.message as string}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            required
                            id='phone_number'
                            label="Numéro de téléphone"
                            {...register('phone_number', {pattern: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im})}
                            autoComplete='off'
                            error={errors?.phone_number !== undefined}
                            helperText={errors?.phone_number && errors.phone_number.message as string}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
                <Typography variant="h5">Identité</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 3}}>
                    <Grid item xs={2}>
                        <TextField
                            id='ssn'
                            label='Nº sécurité sociale'
                            {...register('ssn', {pattern: /^[0-9]+$/})}
                            autoComplete='off'
                            error={errors?.ssn !== undefined}
                            helperText={errors?.ssn && errors.ssn.message as string}
                        />
                        {watch('ssn') && !/^[0-9]{15}$/.test(watch('ssn')) && (
                          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                            Un numéro de sécurité sociale définitif doit contenir exactement 15 chiffres.
                          </Typography>
                        )}
                    </Grid>
                    <GridBreak />
                    <Grid item xs={1}>
                        <FieldAutocompleteList
                            control={control}
                            options={genderOptions}
                            fieldName='gender'
                            labelId='gender'
                            label="Sexe"
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <FieldAutocompleteList
                            control={control}
                            options={genderTitleOptions}
                            fieldName='gender_title'
                            labelId='gender_title'
                            label="Civilité"
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <FieldAutocompleteList
                            control={control}
                            options={countryOption}
                            fieldName='citizenship'
                            labelId='citizenship'
                            label="Nationalité"
                        />
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
                <Typography variant="h5">Informations de naissance</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 3, xl: 4}}>
                    <Grid item xs={1}>
                        <FieldDatepicker
                            control={control}
                            label='Date de naissance'
                            fieldName='birthdate'
                        />
                    </Grid>
                    <GridBreak />
                    <Grid item xs={1}>
                        <TextField
                            id='birth_place'
                            label='Commune de naissance'
                            {...register('birth_place')}
                            autoComplete='off'
                            error={errors?.birth_place !== undefined}
                            helperText={errors?.birth_place && errors.birth_place.message as string}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='birth_department'
                            label='Département de naissance'
                            {...register('birth_department', {pattern: /^[0-9]{2}$/})}
                            autoComplete='off'
                            error={errors?.birth_department !== undefined}
                            helperText={errors?.birth_department && errors.birth_department.message as string}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <FieldAutocompleteList
                            control={control}
                            options={countryOption}
                            fieldName='birth_country'
                            labelId='birth_country'
                            label="Pays de naissance"
                        />
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
                <Typography variant="h5">Foyer familial</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 3, xl: 4}}>
                    <Grid item xs={1}>
                        <FieldAutocompleteList
                            control={control}
                            options={maritalStatusOptions}
                            fieldName='marital_status'
                            labelId='marital_status'
                            label="Situation familiale"
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='dependents'
                            label="Enfants à charge"
                            type='number'
                            InputProps={{
                                inputProps: { min: 0 }
                            }}
                            {...register('dependents', {pattern: /^[0-9]+$/})}
                            autoComplete='off'
                            error={errors?.dependents !== undefined}
                            helperText={errors?.dependents && errors.dependents.message as string}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
                <Typography variant="h5">Localisation</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 3}}>
                    <Grid item xs={1} sm={2}>
                        <TextField
                            id='address'
                            label='Adresse postale entière'
                            {...register('address.address')}
                            autoComplete='off'
                            error={errors?.address && (errors?.address as any).address !== undefined}
                            helperText={errors?.address && (errors.address as any)?.address && (errors.address as any).address.message as string}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        {isAgenciesLoading && <CircularProgress size={10}/>}
                        {!isAgenciesLoading && isAgenciesError && <Typography color={"red"}>Une erreur est survenue</Typography>}
                        {!isAgenciesLoading && !isAgenciesError && (
                            <FieldAutocompleteList
                                control={control}
                                options={agenciesData.reduce((a: any, v: any) => {a.push([v.id, v.closed ? `${v.name} (fermée)` : v.name]); return a;}, [])}
                                fieldName='agency_id'
                                labelId='agency_id'
                                label="Agence"
                            />
                        )}
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='address_street'
                            label='Adresse (Anael)'
                            {...register('address_street')}
                            autoComplete='off'
                            error={errors?.address_street !== undefined}
                            helperText={errors?.address_street && errors.address_street.message as string}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        {isZCLoading ? (<CircularProgress />) : (!isZCLoading && isZCError) ? (<Typography color={"red"}>Une erreur est survenue.</Typography>) : (
                            <FieldAutocompleteList
                                control={control}
                                options={zipCodesOptions}
                                fieldName='address_zip_code'
                                labelId='address_zip_code'
                                label="Code Postal (Anael)"
                            />
                        )}
                    </Grid>
                    <Grid item xs={1}>
                        {(isCitiesLoading && cityEnabled) ? (<CircularProgress />) : (!isCitiesLoading && !cityEnabled && isCitiesError) ? (<Typography color={"red"}>Une erreur est survenue.</Typography>) : citiesOptions.length > 0 && (
                            <FieldAutocompleteList
                                control={control}
                                options={citiesOptions}
                                fieldName='address_city'
                                labelId='address_city'
                                label="Ville (Anael)"
                            />
                        )}
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
                <Typography variant="h5">Identité</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 2}}>
                    <Grid item xs={1} sx={{paddingTop: 0}}>
                        {isOptionsLoading ? (<CircularProgress />) : isOptionsError ? (<Typography color={"red"}>Une erreur est survenue</Typography>) : (
                            <FieldAutocompleteList
                                control={control}
                                options={valueOptions.options.reduce((a: any, v: any) => {a.push([v.value, v.label]); return a;}, [])}
                                fieldName='id_type'
                                labelId='id_type'
                                label="Type de document"
                            />
                        )}
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='id_number'
                            label="Nº de la pièce d'identité"
                            {...register('id_number')}
                            autoComplete='off'
                            error={errors?.id_number ? true : false}
                            helperText={errors?.id_number ? errors.id_number.message as string : null}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <FieldDatepicker
                            control={control}
                            label="Date de délivrance"
                            fieldName='id_start_date'
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <FieldDatepicker
                            control={control}
                            label="Date d'expiration"
                            fieldName='id_expiration_date'
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            inputProps={{ maxLength: 2 }}
                            id='delivrance_department'
                            label='Département'
                            {...register('id_department')}
                            autoComplete='off'
                            error={errors?.id_department ? true : false}
                            helperText={errors?.id_department ? errors.id_department.message as string : null}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
                <Typography variant="h5">Banque</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 2, xl: 4}}>
                    <Grid item xs={1}>
                        <TextField
                            id='account_holder'
                            label='Nom du titulaire'
                            {...register('account_holder')}
                            autoComplete='off'
                            error={errors?.account_holder ? true : false}
                            helperText={errors?.account_holder ? errors.account_holder.message as string : null}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='domiciliation'
                            label='Domiciliation'
                            {...register('domiciliation')}
                            autoComplete='off'
                            error={errors?.domiciliation ? true : false}
                            helperText={errors?.domiciliation ? errors.domiciliation.message as string : null}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='bank_identifier_code'
                            label='BIC'
                            {...register('bank_identifier_code')}
                            autoComplete='off'
                            error={errors?.bank_identifier_code ? true : false}
                            helperText={errors?.bank_identifier_code ? errors.bank_identifier_code.message as string : null}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='international_bank_acount_number'
                            label='IBAN'
                            {...register('international_bank_acount_number')}
                            autoComplete='off'
                            error={errors?.international_bank_acount_number ? true : false}
                            helperText={errors?.international_bank_acount_number ? errors.international_bank_acount_number.message as string : null}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default ProfileForm;
